import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Drawer,
  Field,
  Input,
  useToast,
} from "../../../design-system";
import { uploadInternalDocumentVersion } from "../../../helpers/internal_documentation_helper";
import { TEXTAREA_STYLE } from "../constants";
import { FileDropInput } from "./FileDropInput";

/**
 * Drawer para subir una nueva versión de un documento existente.
 *
 * Diferencias respecto al `UploadDocumentDrawer`:
 *  - No pide metadatos (code, título, nivel, tipo): se heredan del doc actual.
 *  - Solo archivo + notas de versión opcionales + entrada en vigor opcional.
 *  - El doc nuevo nace como draft con `is_current=false`. La versión
 *    vigente actual no se ve afectada hasta que la nueva se publique.
 */
export function UploadNewVersionDrawer({ open, onClose, currentDoc, onUploaded }) {
  const toast = useToast();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [effectiveAt, setEffectiveAt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setNotes("");
      setEffectiveAt("");
      setError(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [open]);

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!file) return setError("Selecciona el archivo de la nueva versión.");
    if (!currentDoc?.id) return setError("No se pudo identificar el documento.");

    setSubmitting(true);
    try {
      await uploadInternalDocumentVersion({
        id: currentDoc.id,
        file,
        version_notes: notes.trim() || undefined,
        effective_at: effectiveAt || undefined,
      });
      toast.success("Nueva versión subida como borrador");
      onUploaded?.();
    } catch (e2) {
      setError(typeof e2 === "string" ? e2 : e2?.message || "No se pudo subir la nueva versión.");
    } finally {
      setSubmitting(false);
    }
  };

  const formId = "upload-new-version-form";
  const nextNumber = (currentDoc?.version || 1) + 1;

  return (
    <Drawer
      open={open}
      onClose={submitting ? undefined : onClose}
      title="Subir nueva versión"
      size="md"
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <div style={{ flex: 1 }} />
          <Button variant="primary" type="submit" form={formId} icon="Upload" disabled={submitting}>
            {submitting ? "Subiendo…" : `Subir v${nextNumber} como borrador`}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {error && <Alert tone="danger">{error}</Alert>}

        {currentDoc && (
          <Alert tone="info">
            Crearás la <strong>versión {nextNumber}</strong> de{" "}
            <strong>{currentDoc.code} · {currentDoc.title}</strong>. La versión actual seguirá
            siendo la vigente hasta que publiques esta nueva.
          </Alert>
        )}

        <Field label="Archivo de la nueva versión" required>
          <FileDropInput file={file} inputRef={fileRef} onFileChange={onFileChange} />
        </Field>

        <Field label="Notas de versión" help="Qué cambia respecto a la anterior. Visible en el historial.">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ej. Actualización del apartado 3 tras revisión legal."
            rows={3}
            style={TEXTAREA_STYLE}
          />
        </Field>

        <Field label="Entrada en vigor" help="Opcional. Puedes definirla ahora o al publicar.">
          <Input
            type="date"
            value={effectiveAt}
            onChange={(e) => setEffectiveAt(e.target.value)}
          />
        </Field>
      </form>
    </Drawer>
  );
}
